/**
 * Hero banner copy + creative: identity, defaults, validation, and the save seam.
 *
 * The Content Management hero section used to be a pure picker — one ship or
 * destination out of the brief list, and the store visit page drew the title from
 * the item and hard-coded everything else. Tom's design (41792875) turns each
 * option into an editable banner: its own header, copy, CTA label and CTA link,
 * over either the catalogue image or one the CMS user uploads.
 *
 * Two kinds of banner now exist, and they differ only in where the creative comes
 * from:
 *
 * - **Catalogue** — a ship or destination from `GET_BRIEF_SHIP_DESTINATION_LISTS_V2`.
 *   Its CTA already has somewhere to go (the ship/destination template), so a CTA
 *   URL is optional.
 * - **Bespoke** — created in the CMS from an uploaded image. Nothing else knows how
 *   to route its CTA, so a CTA URL is required.
 *
 * ## Where a bespoke banner lives
 *
 * In `custom_banners`, one row per banner, via `CREATE`/`UPDATE`/`DELETE`/
 * `GET_CUSTOM_BANNERS`. Unlike the ship and destination customisation endpoints
 * this is multi-row — there is no unique key, so an agency or market can hold
 * several banners as separate hero options and CREATE always inserts.
 *
 * A banner carries its own `status`: `draft` (the backend's default) or
 * `published`. Only a published one can be picked as the active hero —
 * `SET_BRIEF_HERO_BANNER_V2` validates that — and only published ones appear in
 * `GET_BRIEF_SHIP_DESTINATION_LISTS_V2`. That is what makes the editor's Save a
 * real draft rather than a publish, and why the CMS list needs `GET_CUSTOM_BANNERS`
 * (which does return drafts) rather than the picker list alone.
 *
 * ## ⚠️ Copy overrides for catalogue items have no backend
 *
 * `custom_banners` is for bespoke banners only. A ship or destination draws its
 * `name`/`description` from `ship_trades` / `destination_trade_enhancements`, and
 * nothing stores a custom header, copy or CTA against one —
 * `GET_BRIEF_HERO_BANNER_V2` is explicit that `cta_text`/`cta_url` exist for the
 * `bespoke` category and never for the other two. The design's per-card "Edit
 * banner" affordance therefore has nothing behind it on a catalogue card; that is
 * an open question, not an oversight here.
 */

import { apiRequest, getUserDetails } from "./apiRequest";
import { requestType } from "./requests";
import { buildReadScopeData, buildScopeData } from "./contentManagementScope";
import { isSaveOk, saveErrorMessage } from "./contentManagementSaveResult";

// ---------------------------------------------------------------------- limits

/**
 * Character allowances, straight off the design.
 *
 * Unlike `CHAR_LIMITS` in `constants/editFieldHelpers.js` — where the numbers were
 * inferred from Figma for fields whose real limits were never supplied, and so are
 * counted but never enforced — these four are drawn on the hero banner design with
 * their exact values, against a banner whose box is fixed. Going over does not
 * truncate gracefully, it overruns the card. So these *are* enforced: the page
 * refuses to save while any field is over, and says which.
 *
 * There is also no way for over-length copy to arrive from elsewhere, because
 * nothing but this form writes these fields.
 */
export const HERO_BANNER_LIMITS = {
    header: 40,
    copy: 70,
    ctaText: 20,
};

/** Upload constraints for the banner creative, per the design. */
export const HERO_BANNER_IMAGE = {
    accept: ".jpg",
    maxBytes: 1024 * 1024,
    dimensionsHint: "1250x625px (2:1 aspect ratio)",
    hints: ["Max file size 1MB.", "Only .jpg attachments accepted."],
};

// -------------------------------------------------------------------- identity

/**
 * Stable id for a banner, used as the route param and as the key of the copy map.
 *
 * Catalogue items prefer BE's `key` (`"ship-harmony-of-the-seas"`) because
 * `brief_ship_destination_uid` is unique only within a category — 27 is both
 * Harmony of the Seas and Perfect Day CocoCay, and the hero list is the one list
 * holding both kinds. Falls back to `category:uid` when the response omits `key`,
 * which is the same allowance `contentItemIdentity.isSameContentItem` makes.
 */
export const heroBannerKey = (item) => {
    if (!item) return null;
    if (item.bespokeUid) return `bespoke-${item.bespokeUid}`;
    if (item.key) return item.key;

    const uid = item.brief_ship_destination_uid;
    if (uid === undefined || uid === null) return null;
    return `${item.category ?? "item"}-${uid}`;
};

export const isBespokeHeroBanner = (item) => !!item?.bespokeUid;

// -------------------------------------------------------------------- defaults

/**
 * What the store visit hero said before any of this was editable.
 *
 * These are the literals that were hard-coded into `RoyalMomentumVisitStep2` and
 * `RoyalMomentumKamVisitStep2`, kept verbatim so a brief with no custom copy —
 * every brief, until someone saves one — renders exactly as it does today. The
 * header additionally prefers the item's own title, which is what those pages
 * already did.
 */
export const HERO_BANNER_FALLBACK = {
    header: "Legend of the Seas — Barcelona 2026",
    copy: "A big opportunity for your customers — and strong booking potential",
    ctaText: "Explore",
};

/**
 * The copy and creative to render for a banner, custom where set and the original
 * hard-coded text everywhere else.
 *
 * This is the single fallback rule: the CMS preview, the store visit hero and the
 * KAM visit hero all call it, so none of the three can drift into showing a
 * different default from the others — which is exactly what had already happened
 * to the scrim (`RoyalMomentumVisitStep2` on a navy gradient, KAM still on a flat
 * grey wash).
 *
 * Blank strings fall through to the default rather than rendering as empty: an
 * untouched field and a deliberately-cleared one are indistinguishable here, and
 * an empty hero headline is never the better reading of either.
 *
 * `image` stays null when there is nothing to show; the card supplies the bundled
 * backdrop, so no image asset has to be imported into this module.
 */
export const resolveHeroBannerContent = (hero, overrides) => ({
    image: overrides?.image || hero?.image || null,
    header: overrides?.header || hero?.title || HERO_BANNER_FALLBACK.header,
    // `hero.description` is the banner's own copy where the backend has one. It is
    // populated for every category, but only a bespoke banner's is authored here —
    // a ship or destination's comes from the trade tables.
    copy: overrides?.copy || hero?.description || HERO_BANNER_FALLBACK.copy,
    // `cta_text`/`cta_url` exist on the bespoke category only; the other two never
    // carry them, so these fall through to the default label and to routing by
    // category, exactly as before bespoke banners existed.
    ctaText: overrides?.ctaText || hero?.ctaText || HERO_BANNER_FALLBACK.ctaText,
    ctaUrl: overrides?.ctaUrl || hero?.ctaUrl || null,
});

/**
 * A catalogue item (ship or destination) as banner form values.
 *
 * "Edit banner" on a catalogue card does not edit the ship — nothing stores copy
 * against one. It opens a **new bespoke banner** pre-filled from it, so the user
 * starts from that ship's artwork and name instead of a blank form. The result is a
 * separate option in the picker; the original catalogue entry is untouched.
 *
 * ⚠️ `image` is the trade image URL, not a HubSpot CDN one. `CREATE_CUSTOM_BANNER`
 * documents its `image` as "a HubSpot CDN URL (upload via UPLOAD_ASSET_TO_HUBSPOT
 * first)" — it only rejects an *absent* image, so this stores, but confirm the
 * backend is happy hosting a trade URL here rather than silently expecting its own
 * CDN. Replacing the image in the form sidesteps it entirely.
 */
export const heroBannerSeedFromCatalogue = (item) => ({
    image: item?.image ?? "",
    header: item?.title ?? "",
    copy: item?.description ?? "",
    ctaText: "",
    ctaUrl: "",
});

/**
 * Blank form state for a banner, pre-filled from whatever is already saved.
 *
 * `fillDefaults` seeds the standing default copy where the record carries none —
 * used when opening an existing banner, not when starting a blank one.
 *
 * Why it is worth doing rather than leaving the field empty: the preview already
 * renders `HERO_BANNER_FALLBACK.copy` for an empty copy (that is what the visit
 * pages have always shown), so an empty field put the form and the preview beside
 * each other saying different things. And `CREATE_CUSTOM_BANNER` requires `copy` —
 * a ship whose `bold_intro_copy` is blank would otherwise open a form that cannot
 * be saved until the user invents a line. Seeding the default makes the two agree
 * and the banner saveable, with the text there to edit rather than to guess at.
 *
 * A blank new banner is deliberately left blank: the design gives that form
 * placeholder text ("Enter banner copy here"), and pre-filling it would read as
 * content someone had already written.
 */

export const heroBannerFormValues = (hero, overrides, { fillDefaults = false } = {}) => ({
    image: overrides?.image
        ? { name: heroBannerImageName(overrides.image), url: overrides.image }
        : "",
    header: overrides?.header ?? "",
    copy: overrides?.copy || (fillDefaults ? HERO_BANNER_FALLBACK.copy : ""),
    ctaText: overrides?.ctaText ?? "",
    ctaUrl: overrides?.ctaUrl ?? "",
});

/** Filename for an already-hosted creative, so the upload control has a label. */
const heroBannerImageName = (url) => {
    try {
        return decodeURIComponent(String(url).split("?")[0].split("/").pop()) || "Current image";
    } catch {
        return "Current image";
    }
};

// ------------------------------------------------------------------ validation

/**
 * `http(s)` only, and it must parse. A CTA that silently points at
 * `javascript:` or a bare `www.` string is worse than one the form rejected.
 *
 * Whitespace is rejected outright rather than trimmed — leading/trailing
 * whitespace is handled by `sanitiseCtaUrl` on the input side, so anything the
 * validator still sees is an internal space (a pasted string that split
 * across a line, or a URL with a stray space someone did not notice) and
 * marks the whole value as broken rather than silently normalising it.
 */
const isUsableUrl = (value) => {
    const raw = String(value);
    if (/\s/.test(raw)) return false;
    try {
        const url = new URL(raw);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
};

/**
 * Strips whitespace from a CTA URL as it is typed or pasted.
 *
 * Kept aggressive on purpose — a URL has no legal whitespace anywhere, so a
 * space in the middle is either a paste that folded across a line or a typo,
 * neither of which should be preserved into the field. Called from the input's
 * change handler so the visible value never carries whitespace.
 */
export const sanitiseCtaUrl = (value) => String(value ?? "").replace(/\s+/g, "");

/**
 * Field-keyed error messages, empty when the banner is saveable.
 *
 * `isBespoke` decides the two rules that differ by kind: a bespoke banner has no
 * catalogue creative to fall back to and no template for its CTA to reach, so both
 * the image and the CTA URL become required.
 */
export const validateHeroBanner = (values, { isBespoke = false } = {}) => {
    const errors = {};
    const trimmed = (key) => String(values?.[key] ?? "").trim();

    const header = trimmed("header");
    if (isBespoke && !header) {
        errors.header = "Enter a banner header.";
    } else if (header.length > HERO_BANNER_LIMITS.header) {
        errors.header = `Keep the header to ${HERO_BANNER_LIMITS.header} characters.`;
    }

    // Required, not optional: `CREATE_CUSTOM_BANNER` answers
    // `ERROR_MISSING_BANNER_COPY` without it. This was the other way round while the
    // endpoint was still a stub and the only guide was the design, which shows the
    // field filled but says nothing about whether it may be left empty.
    const copy = trimmed("copy");
    if (isBespoke && !copy) {
        errors.copy = "Enter the banner copy.";
    } else if (copy.length > HERO_BANNER_LIMITS.copy) {
        errors.copy = `Keep the banner copy to ${HERO_BANNER_LIMITS.copy} characters.`;
    }

    if (trimmed("ctaText").length > HERO_BANNER_LIMITS.ctaText) {
        errors.ctaText = `Keep the CTA text to ${HERO_BANNER_LIMITS.ctaText} characters.`;
    }

    // Optional on both sides — the backend documents "a banner may have no CTA at
    // all", so an empty one is a banner without a button rather than an error. Only
    // the shape is checked, and only when something was typed.
    const ctaUrl = trimmed("ctaUrl");
    if (ctaUrl && !isUsableUrl(ctaUrl)) {
        errors.ctaUrl = "Enter a full URL, starting http:// or https://.";
    }

    const image = values?.image;
    // A file field holds `{ name, url, uploading, error }`; `url` only appears once
    // the upload lands, which is also what makes "still uploading" detectable.
    const imageUrl = typeof image === "object" && image !== null ? image.url : image;
    if (isBespoke && !imageUrl) {
        errors.image =
            typeof image === "object" && image?.uploading
                ? "Wait for the image to finish uploading."
                : "Upload a banner image.";
    }

    return errors;
};

/** Whether an upload is still in flight, which holds Save shut. */
export const isHeroBannerUploading = (values) =>
    typeof values?.image === "object" && values.image !== null && !!values.image.uploading;

/**
 * Form state → the shape stored against a banner key.
 *
 * Blanks are kept as empty strings rather than dropped: clearing a field has to be
 * able to undo a previous save, and `resolveHeroBannerContent` reads an empty
 * string as "use the default" anyway.
 */
export const collectHeroBannerContent = (values) => {
    const image = values?.image;
    const imageUrl = typeof image === "object" && image !== null ? image.url : image;

    return {
        image: imageUrl || "",
        header: String(values?.header ?? "").trim(),
        copy: String(values?.copy ?? "").trim(),
        ctaText: String(values?.ctaText ?? "").trim(),
        ctaUrl: String(values?.ctaUrl ?? "").trim(),
    };
};

// ------------------------------------------------------------------- CTA target

/**
 * Where the hero's CTA should go, as a descriptor the caller acts on.
 *
 * Three cases, in priority order:
 *
 * 1. A **custom CTA URL** wins wherever one is set — it is the only thing a
 *    bespoke banner can route by, and on a catalogue banner it is an explicit
 *    override of the template link.
 * 2. Otherwise a **catalogue banner** routes to its ship or destination template,
 *    exactly as both visit pages did before any of this was editable. Ships carry
 *    their `shipUid` so the template opens on the right one.
 * 3. A banner with neither — a bespoke one whose URL has been cleared — returns
 *    `null`, and the caller leaves the button inert rather than navigating
 *    somewhere arbitrary.
 *
 * `base` differs per page (`store-visit` vs `kam-visit`), which is why it is
 * passed in rather than known here.
 */
export const heroBannerCtaTarget = (hero, content, { base, storeQuery = "" } = {}) => {
    const url = content?.ctaUrl;
    if (url) return { kind: "external", url };

    if (!hero?.type) return null;

    if (hero.category === "ship") {
        const shipQuery = storeQuery
            ? `${storeQuery}&shipUid=${hero.sourceUid}`
            : `?shipUid=${hero.sourceUid}`;
        return { kind: "route", path: `${base}/${hero.type}/${hero.slug}/leisure${shipQuery}` };
    }

    return { kind: "route", path: `${base}/${hero.type}/${hero.slug}/leisure${storeQuery}` };
};

/**
 * Follow a target from `heroBannerCtaTarget`.
 *
 * A custom URL opens in a new tab: it can point anywhere, and an agent part-way
 * through a store visit must not lose the visit to a link in a banner. `noopener`
 * because the opened page is not ours to trust with a window handle.
 */
export const followHeroBannerCta = (target, navigate) => {
    if (!target) return;
    if (target.kind === "external") {
        window.open(target.url, "_blank", "noopener,noreferrer");
        return;
    }
    navigate(target.path);
};

// ----------------------------------------------------------------- persistence
//
// `custom_banners` CRUD. A bespoke banner is a row of its own, not an override
// laid over a catalogue item — which is why these take and return a `uid` rather
// than the `bannerKey` the rest of this module uses for identity.

/** Form state -> the backend's field names. */
const bannerPayload = (content) => ({
    image: content?.image || "",
    header: content?.header || "",
    copy: content?.copy || "",
    cta_text: content?.ctaText || "",
    cta_url: content?.ctaUrl || "",
});

/** A `custom_banners` row -> the shape the picker and the form read. */
export const fromCustomBanner = (row) => ({
    bespokeUid: row?.uid,
    category: "bespoke",
    status: row?.status ?? "draft",
    scope: row?.scope ?? "agency",
    title: row?.header ?? "",
    image: row?.image ?? null,
    header: row?.header ?? "",
    copy: row?.copy ?? "",
    // Mirrors `copy` under the name the brief response uses, so
    // `resolveHeroBannerContent` reads a banner from either source the same way.
    description: row?.copy ?? "",
    ctaText: row?.cta_text ?? "",
    ctaUrl: row?.cta_url ?? "",
    // BE's own list key. Absent on this endpoint, so it is left null and
    // `heroBannerKey` falls back to `bespoke-<uid>` — which is what the CMS
    // addresses a banner by anyway, since a draft has no published list entry.
    key: null,
    brief_ship_destination_uid: null,
});

/**
 * Create a bespoke banner.
 *
 * Always an INSERT — `custom_banners` has no unique key, so one agency or market
 * can hold several banners as separate hero options. `status` defaults to `draft`
 * backend-side, and a draft cannot be picked as the active hero
 * (`SET_BRIEF_HERO_BANNER_V2` validates `status="published"`), which is what makes
 * the editor's Save a genuine draft rather than a publish.
 */
export const createCustomBanner = async ({ scope, content, status = "draft" }) => {
    const response = await apiRequest({
        requestType: requestType.createCustomBanner,
        payload: {
            data: {
                user_uid: getUserDetails()?.user_details?.uid,
                // `scope: "market"` + `market_code`, or `scope: "agency"` +
                // `agency_id`. The backend wants no `agency_id` at market level, which
                // is exactly what this builder omits.
                ...buildScopeData(scope),
                ...bannerPayload(content),
                status,
            },
        },
    });
    return bannerResult(response);
};

/**
 * Update a bespoke banner's content or status.
 *
 * Every content field is optional to the backend and an omitted one keeps its
 * stored value — but the form always holds the complete banner, so all of them are
 * sent. `scope`/`agency_id`/`market_code` are the row's identity and cannot be
 * changed here; moving a banner between levels is a delete and a re-create.
 */
export const updateCustomBanner = async ({ uid, content, status }) => {
    const response = await apiRequest({
        requestType: requestType.updateCustomBanner,
        payload: {
            data: {
                user_uid: getUserDetails()?.user_details?.uid,
                uid,
                ...bannerPayload(content),
                // Omitted rather than sent empty, so "save my edits" does not also
                // silently unpublish a live banner.
                ...(status ? { status } : {}),
            },
        },
    });
    return bannerResult(response);
};

/** Remove a bespoke banner. Hard delete — there is no restore. */
export const deleteCustomBanner = async ({ uid }) => {
    const response = await apiRequest({
        requestType: requestType.deleteCustomBanner,
        payload: { data: { user_uid: getUserDetails()?.user_details?.uid, uid } },
    });
    return bannerResult(response);
};

/**
 * Every bespoke banner selectable for the caller — the agency's own plus its
 * resolved market's, **drafts included**.
 *
 * ⚠️ **Nothing calls this, deliberately.** The CMS reads its options from
 * `GET_BRIEF_SHIP_DESTINATION_LISTS_V2`, which already blends published bespoke
 * banners in with the ship and destination catalogue — one list rather than two
 * merged on the client. Kept because it is the only way to reach a **draft**
 * banner, which nothing in the current flow creates: the editor saves banners
 * `published`, since a draft would be invisible to the blended list and therefore
 * unselectable. Wire this up if drafts are ever introduced.
 *
 * Also worth knowing before that day: it is agency-keyed like the other reads in
 * this feature — it takes `agency_id` (or resolves one from `user_uid`) and finds
 * the market from there, documenting no `market_code` input. At all-accounts level
 * it would answer for whichever agency the signed-in user resolves to rather than
 * for the market being edited.
 */
export const fetchCustomBanners = async ({ scope } = {}) => {
    const response = await apiRequest({
        requestType: requestType.getCustomBanners,
        payload: {
            data: {
                user_uid: getUserDetails()?.user_details?.uid,
                ...buildReadScopeData(scope),
            },
        },
    });

    if (!isSaveOk(response)) {
        return { ok: false, banners: [], error: saveErrorMessage(response) };
    }

    const rows = response?.data?.data?.banners ?? [];
    return { ok: true, error: null, banners: rows.map(fromCustomBanner) };
};

/**
 * The `{ ok, message }` shape every call site here branches on.
 *
 * The backend answers 200 with `status: 1` on failure rather than throwing, so a
 * write is only known to have landed if something reads that field — the same
 * reason `contentManagementSaveResult` exists for the section saves.
 */
const bannerResult = (response) => {
    if (!isSaveOk(response)) {
        return { ok: false, banner: null, message: saveErrorMessage(response) };
    }
    const row = response?.data?.data ?? null;
    return { ok: true, message: null, banner: row ? fromCustomBanner(row) : null, uid: row?.uid };
};
